import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { AdministratorCompanyCRUDService, AuthenticationService, OAuth2Token } from '@digitalforest/ngx-oak-api';

import { TokenExpirationService } from '../../services/token-expiration.service';
import { getHomeRoute, getLoginRoute } from '../app.common';
import { AdministratorActions } from './administrator.actions';
import { ApiKeyInfoActions } from './api-key-info.actions';
import { ScopeActions } from './scope.actions';
import { TokenActions } from './token.actions';
import { TokenData } from './token.feature';

import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable()
export class TokenEffects {
  private actions$: Actions = inject(Actions);
  private router: Router = inject(Router);
  private administratorCompanyCRUDService: AdministratorCompanyCRUDService = inject(AdministratorCompanyCRUDService);
  private authenticationService: AuthenticationService = inject(AuthenticationService);
  private jwtHelperService: JwtHelperService = inject(JwtHelperService);
  private tokenExpirationService: TokenExpirationService = inject(TokenExpirationService);

  startTokenMonitoring$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TokenActions.loadTokenSuccess, TokenActions.loginSuccess),
        tap(() => {
          console.debug('[TokenEffects] Starting token expiration monitoring due to token success.');
          this.tokenExpirationService.startMonitoring();
        }),
      ),
    { dispatch: false },
  );

  stopTokenMonitoring$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TokenActions.logout, TokenActions.loadTokenFailure, TokenActions.loginFailure),
        tap((action) => {
          console.debug(`[TokenEffects] Stopping token expiration monitoring due to action: ${action.type}`);
          this.tokenExpirationService.stopMonitoring();
        }),
      ),
    { dispatch: false },
  );

  loginFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TokenActions.loginFailure),
        tap(() => console.debug('[TokenEffects] loginFailure')),
        tap((action) => console.error(`[TokenEffects] loginFailure: error = ${JSON.stringify(action.error)}`)),
      ),
    { dispatch: false },
  );

  loadTokenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TokenActions.loadTokenSuccess),
      tap(() => console.debug('[TokenEffects] loadTokenSuccess')),
      map((_action) => {
        const company_id = localStorage.getItem('company_id');
        if (company_id) {
          console.debug(`[TokenEffects] Found company_id ${company_id} in localStorage, loading company scope`);
          return ScopeActions.loadCompany({ companyId: parseInt(company_id) });
        }
        console.debug('[TokenEffects] No company_id found in localStorage after token load');
        return { type: '[TokenEffects] No company to load' };
      }),
    ),
  );

  loadTokenFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TokenActions.loadTokenFailure),
        tap(() => console.debug('[TokenEffects] loadTokenFailure')),
        tap((action) => console.error(`[TokenEffects] loadTokenFailure: error = ${JSON.stringify(action.error)}`)),
        tap(() => {
          console.debug('[TokenEffects] Navigating to login page');
          this.router.navigate(getLoginRoute()).then(
            () => console.debug('[TokenEffects] Navigation to login successful'),
            (error) => console.error('[TokenEffects] Navigation to login failed:', error),
          );
        }),
      ),
    { dispatch: false },
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TokenActions.logoutSuccess),
        tap(() => console.debug('[TokenEffects] logoutSuccess')),
        tap(() => {
          // Ensure we remove the token before navigation
          localStorage.removeItem('authentication');
          // Navigate and log any errors
          this.router.navigate(getLoginRoute()).then(
            () => console.debug('[TokenEffects] Navigation to login successful'),
            (error) => console.error('[TokenEffects] Navigation to login failed:', error),
          );
        }),
      ),
    { dispatch: false },
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TokenActions.loginSuccess),
      tap(() => console.debug(`[TokenEffects] loginSuccess`)),
      tap(() => {
        // Clear the stored company_id after successful login
        // This ensures the admin is redirected to company selection page after login
        localStorage.removeItem('company_id');
        console.debug('[TokenEffects] loginSuccess: cleared company_id from localStorage');
      }),
      switchMap((action) => {
        // After login success, get administrator companies
        return this.administratorCompanyCRUDService.manageGetAllAdministratorCompaniesByAdministrator(action.tokenData.id).pipe(
          map((administratorCompanyList) => {
            const sortedList = [...administratorCompanyList].sort((a, b) => a.company_name.localeCompare(b.company_name));

            // If there's exactly one company, load it directly
            if (sortedList.length === 1) {
              const companyId = sortedList[0].company_id;
              this.router.navigate(getHomeRoute()).then();
              return ScopeActions.loadCompany({ companyId });
            }

            // Otherwise navigate to company selection
            this.router.navigate(getHomeRoute()).then();
            return { type: '[TokenEffects] Multiple companies available' };
          }),
        );
      }),
    ),
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TokenActions.logout),
      tap(() => console.debug('[TokenEffects] logout')),
      switchMap(() =>
        this.authenticationService.logout().pipe(
          tap(() => localStorage.removeItem('authentication')),
          switchMap(() =>
            of(AdministratorActions.unload(), ScopeActions.unloadCompany(), ApiKeyInfoActions.unload(), TokenActions.logoutSuccess()),
          ),
          catchError((error) => of(TokenActions.logoutFailure({ error }))),
        ),
      ),
    ),
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TokenActions.login),
      tap(() => console.debug('[TokenEffects] login')),
      switchMap((action) =>
        this.authenticationService.loginForAccessToken(action.email, action.password).pipe(
          switchMap((token: OAuth2Token) => {
            console.debug(`[TokenEffects] login: token = ${JSON.stringify(token)}`);
            localStorage.setItem('authentication', token.access_token);
            const tokenData = this.jwtHelperService.decodeToken<TokenData>(token.access_token);
            return of(
              TokenActions.loginSuccess({ tokenString: token.access_token, tokenData }),
              AdministratorActions.load({ administratorId: tokenData.id }),
            );
          }),
          catchError((error) => of(TokenActions.loginFailure({ error }))),
        ),
      ),
    ),
  );

  loadToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TokenActions.loadToken),
      tap(() => console.log('[TokenEffects] loadToken - Starting token load process')),
      switchMap(() => {
        const tokenString = localStorage.getItem('authentication');
        console.log(`[TokenEffects] loadToken: tokenString ${tokenString ? 'exists' : 'does not exist'} in localStorage`);

        if (tokenString) {
          try {
            const isExpired = this.jwtHelperService.isTokenExpired(tokenString);
            console.log(`[TokenEffects] loadToken: token expiration check result: ${isExpired ? 'expired' : 'valid'}`);

            if (isExpired) {
              console.log('[TokenEffects] loadToken: Token is expired, dispatching loadTokenFailure');
              return of(TokenActions.loadTokenFailure({ error: 'Token is expired' }));
            } else {
              const tokenData = this.jwtHelperService.decodeToken<TokenData>(tokenString);
              console.log(`[TokenEffects] loadToken: Token successfully decoded, ID: ${tokenData.id}`);
              console.log('[TokenEffects] loadToken: Dispatching loadTokenSuccess and AdministratorActions.load');

              return of(
                TokenActions.loadTokenSuccess({ tokenString, tokenData }),
                AdministratorActions.load({ administratorId: tokenData.id }),
              );
            }
          } catch (error) {
            console.error('[TokenEffects] loadToken: Error processing token:', error);
            return of(TokenActions.loadTokenFailure({ error: `Error processing token: ${error}` }));
          }
        } else {
          console.log('[TokenEffects] loadToken: No token in local storage, dispatching loadTokenFailure');
          return of(TokenActions.loadTokenFailure({ error: 'No token in local storage' }));
        }
      }),
      catchError((error) => {
        console.error('[TokenEffects] loadToken: Caught error in effect:', error);
        return of(TokenActions.loadTokenFailure({ error }));
      }),
    ),
  );
}
